import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { StudioMember } from '@qs/shared';
import { UNAUTHORIZED_EVENT, type UnauthorizedDetail } from '@qs/ui/lib/api';
import { ApiError, api } from './api';

type Auth = { member: StudioMember | null; loading: boolean; refresh: () => Promise<void>; logout: () => Promise<void> };
const Ctx = createContext<Auth>({ member: null, loading: true, refresh: async () => {}, logout: async () => {} });

export function StudioAuthProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<StudioMember | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ member: StudioMember }>('/me');
      setMember(r.member);
    } catch (e) {
      if (!(e instanceof ApiError) || e.status === 401) setMember(null);
    } finally {
      setLoading(false);
    }
  }, []);
  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    setMember(null);
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  // ログインが切れたら（どの API でも 401）、ログイン画面へ移す。タブに戻ったときも確かめる（2026-10-12）
  useEffect(() => {
    const onUnauthorized = (e: Event) => {
      if ((e as CustomEvent<UnauthorizedDetail>).detail.base === '/api/studio') setMember(null);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);
  return <Ctx.Provider value={{ member, loading, refresh, logout }}>{children}</Ctx.Provider>;
}

export const useStudio = () => useContext(Ctx);

/** ログインしていなければログイン画面へ。admin なら管理者だけ */
export function RequireMember({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const { member, loading } = useStudio();
  const loc = useLocation();
  if (loading) return <div style={{ padding: 32, color: 'var(--color-ink-muted)' }}>読み込み中…</div>;
  if (!member) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && member.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}
